import { useState, useRef, useEffect } from "react";
import type { Todo } from "../types";
import { uid } from "../utils/storage";

interface TodoListProps {
  todos: Todo[];
  setTodos: (updater: (prev: Todo[]) => Todo[]) => void;
  activeTodoId: string | null;
  setActiveTodoId: (id: string | null) => void;
  /** 显示添加区域 + 标题（嵌入式主页用 false） */
  showHeader?: boolean;
}

interface VisibleSortedItem {
  todo: Todo;
  rank: number; // 排序权重，0 最高
}

export default function TodoList({
  todos,
  setTodos,
  activeTodoId,
  setActiveTodoId,
  showHeader = true
}: TodoListProps) {
  const [input, setInput] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const editRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editingId && editRef.current) {
      editRef.current.focus();
      editRef.current.select();
    }
  }, [editingId]);

  // 排序：active 任务置顶 → 未完成 → 已完成
  const sorted: VisibleSortedItem[] = [...todos]
    .map((todo) => ({
      todo,
      rank: todo.id === activeTodoId
        ? 0
        : !todo.completed
        ? 1
        : 2
    }))
    .sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      return b.todo.createdAt - a.todo.createdAt;
    });

  const activeCount = todos.filter((t) => !t.completed).length;
  const completedCount = todos.length - activeCount;

  function addTodo(e: React.FormEvent) {
    e.preventDefault();
    const title = input.trim();
    if (!title) return;
    setTodos((prev) => [
      {
        id: uid(),
        title,
        completed: false,
        completedPomodoros: 0,
        createdAt: Date.now()
      },
      ...prev
    ]);
    setInput("");
  }

  function toggle(id: string) {
    setTodos((prev) => prev.map((t) => (t.id === id ? { ...t, completed: !t.completed } : t)));
    if (activeTodoId === id) setActiveTodoId(null);
  }

  function remove(id: string) {
    setTodos((prev) => prev.filter((t) => t.id !== id));
    if (activeTodoId === id) setActiveTodoId(null);
  }

  function clearCompleted() {
    setTodos((prev) => prev.filter((t) => !t.completed));
  }

  function startEdit(t: Todo) {
    setEditingId(t.id);
    setEditingText(t.title);
  }

  function commitEdit() {
    if (!editingId) return;
    const title = editingText.trim();
    setTodos((prev) =>
      prev.map((t) => (t.id === editingId ? { ...t, title: title || t.title } : t))
    );
    setEditingId(null);
    setEditingText("");
  }

  function cancelEdit() {
    setEditingId(null);
    setEditingText("");
  }

  return (
    <div className="flex flex-col gap-3">
      {/* 添加任务 */}
      <form onSubmit={addTodo} className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="新增任务，回车添加 ..."
          className="flex-1 px-3.5 py-2.5 rounded-lg border border-line bg-paper outline-none text-ink placeholder:text-soft focus:border-focus focus:ring-4 focus:ring-focus-soft"
        />
        <button
          type="submit"
          disabled={!input.trim()}
          className="px-4 py-2.5 rounded-lg bg-focus shadow-insetBtn text-white font-medium hover:bg-focus-hover disabled:opacity-40 disabled:cursor-not-allowed"
        >
          添加
        </button>
      </form>

      {/* 列表 */}
      <ul className="flex flex-col gap-1.5 max-h-[420px] overflow-y-auto pr-1">
        {sorted.length === 0 && (
          <li className="text-center text-sm text-soft py-10 bg-chip rounded-lg border border-hairline">
            还没有任务，添加一个开始专注吧
          </li>
        )}
        {sorted.map(({ todo: t, rank }) => {
          const isEditing = editingId === t.id;
          const isActive = activeTodoId === t.id && !t.completed;
          return (
            <li
              key={t.id}
              className={`group flex items-center gap-3 px-3.5 py-2.5 rounded-lg border ${
                isActive
                  ? "border-focus bg-focus-tint shadow-ring"
                  : rank === 2
                  ? "border-hairline bg-chip opacity-75 hover:opacity-100"
                  : "border-hairline bg-chip hover:bg-chip2"
              }`}
            >
              {/* 完成勾选 */}
              <button
                onClick={() => toggle(t.id)}
                className={`flex-none w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  t.completed
                    ? "bg-short border-short text-white"
                    : "border-soft hover:border-focus"
                }`}
              >
                {t.completed && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12l5 5L20 7"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>

              {/* 标题 / 编辑框 */}
              {isEditing ? (
                <input
                  ref={editRef}
                  type="text"
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitEdit();
                    else if (e.key === "Escape") cancelEdit();
                  }}
                  className="flex-1 px-2 py-1 rounded border border-focus outline-none text-ink bg-paper"
                />
              ) : (
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  {isActive && (
                    <span className="relative flex w-2 h-2 flex-none">
                      <span className="absolute inset-0 rounded-full bg-focus opacity-60 animate-ping" />
                      <span className="relative w-2 h-2 rounded-full bg-focus" />
                    </span>
                  )}
                  <span
                    onDoubleClick={() => startEdit(t)}
                    className={`flex-1 truncate select-none ${
                      t.completed ? "line-through text-soft" : "text-ink"
                    }`}
                    title="双击编辑"
                  >
                    {t.title}
                  </span>
                </div>
              )}

              {/* 已完成番茄 */}
              {t.completedPomodoros > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] text-focus bg-focus-soft px-2 py-0.5 rounded-full font-medium tabular-nums">
                  <span>·</span>
                  <span>{t.completedPomodoros}</span>
                </span>
              )}

              {/* 设置 / 取消 专注 */}
              {!t.completed && !isEditing && (
                <button
                  onClick={() => setActiveTodoId(isActive ? null : t.id)}
                  className={`text-xs px-2 py-0.5 rounded ${
                    isActive
                      ? "bg-focus text-white"
                      : "text-soft hover:bg-chip2 hover:text-body"
                  } ${!isActive && "opacity-0 group-hover:opacity-100"}`}
                >
                  {isActive ? "专注中" : "专注"}
                </button>
              )}

              {/* 删除 */}
              {!isEditing && (
                <button
                  onClick={() => remove(t.id)}
                  className="text-soft hover:text-focus opacity-0 group-hover:opacity-100"
                  title="删除任务"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 6l12 12M6 18L18 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {/* 底部 status */}
      {todos.length > 0 && showHeader && (
        <div className="flex items-center justify-between text-xs text-muted px-1 pt-1">
          <span>
            <span className="text-ink font-medium">{activeCount}</span> 项进行中
            <span className="mx-1.5 text-soft">·</span>
            <span>{completedCount} 已完成</span>
          </span>
          {completedCount > 0 && (
            <button onClick={clearCompleted} className="hover:text-focus">
              清除已完成
            </button>
          )}
        </div>
      )}
    </div>
  );
}
